import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { toAbsoluteUrl } from "sr/helpers";
import { ButtonAuth } from "sr/helpers/ui-components/ButtonAuth";

type RegisterPoint = {
  id: string;
  defaultMessage: string;
};

type RegisterOption = {
  titleId: string;
  titleDefault: string;
  points: RegisterPoint[];
  link: string;
  ctaId: string;
  ctaDefault: string;
};

const registerOptions: RegisterOption[] = [
  {
    titleId: "AUTH.REGISTER_OPTIONS.CLEANER.TITLE",
    titleDefault: "Cleaner",
    link: "/create-cleaner",
    ctaId: "AUTH.GENERAL.REGISTER",
    ctaDefault: "Register as Cleaner",
    points: [
      {
        id: "AUTH.REGISTER_OPTIONS.CLEANER.POINT1",
        defaultMessage: "Find & Manage Jobs From Your Phone Anytime",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.CLEANER.POINT2",
        defaultMessage: "Get Paid the Same Day",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.CLEANER.POINT3",
        defaultMessage: "Work Where You Want, When You Want",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.CLEANER.POINT4",
        defaultMessage:
          "Receive Notifications When New Jobs Are Posted In Your Area",
      },
    ],
  },
  {
    titleId: "AUTH.REGISTER_OPTIONS.COMPANY.TITLE",
    titleDefault: "Company",
    link: "/create-company",
    ctaId: "AUTH.GENERAL.REGISTER.COMPANY",
    ctaDefault: "Register as Company",
    points: [
      {
        id: "AUTH.REGISTER_OPTIONS.COMPANY.POINT1",
        defaultMessage: "Find Cleaners in Your Area",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.COMPANY.POINT2",
        defaultMessage: "Post Your Cleaning Jobs & Find Qualified Cleaners",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.COMPANY.POINT3",
        defaultMessage: "Manage Contractors & Post Work Orders",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.COMPANY.POINT4",
        defaultMessage: "Review Work & Submit Payment",
      },
    ],
  },
  {
    titleId: "AUTH.REGISTER_OPTIONS.RESIDENTIAL.TITLE",
    titleDefault: "Residential Customer",
    link: "/registerasindividual",
    ctaId: "AUTH.GENERAL.REGISTER.RESIDENTIAL",
    ctaDefault: "Register as Residential Customer",
    points: [
      {
        id: "AUTH.REGISTER_OPTIONS.RESIDENTIAL.POINT1",
        defaultMessage: "Search for Cleaners in Your Area",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.RESIDENTIAL.POINT2",
        defaultMessage:
          "Message Cleaners Directly & Request Services for Your Home or Apartment",
      },
      {
        id: "AUTH.REGISTER_OPTIONS.RESIDENTIAL.POINT3",
        defaultMessage: "Review Work & Submit Payment",
      },
    ],
  },
];

export default function RegisterOptions() {
  const navigate = useNavigate();
  const intl = useIntl();

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-0">
        <div className="bg-white border border-gray-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-8 lg:px-12 lg:py-12">
            <div className="flex justify-center mb-10 lg:mb-12">
              <Link to="/" className="inline-block">
                <img
                  width={160}
                  src={toAbsoluteUrl("/media/logos/logo2.png")}
                  alt="Logo"
                  className="bg-white rounded-lg"
                />
              </Link>
            </div>

            <div className="text-center mb-10 lg:mb-12">
              <h1 className="mt-10 text-center text-3xl font-bold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                <FormattedMessage
                  id="AUTH.REGISTER_OPTIONS.HEADER"
                  defaultMessage="Register"
                />
              </h1>
              <p className="mt-3 text-gray-600">
                <FormattedMessage
                  id="AUTH.REGISTER_OPTIONS.SUBTITLE"
                  defaultMessage="Choose your account type to get started"
                />
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {registerOptions.map((option) => (
                <div key={option.titleId} className="h-full">
                  <div className="h-full border border-gray-border rounded-xl p-6 lg:p-8  transition-all duration-200 bg-white hover:shadow-md flex flex-col">
                    <div className="mb-6">
                      <div className="text-xl font-semibold text-[#0078ae]">
                        {intl.formatMessage({
                          id: option.titleId,
                          defaultMessage: option.titleDefault,
                        })}
                      </div>
                    </div>

                    <ul className="space-y-4 flex-1">
                      {option.points.map((point) => (
                        <li key={point.id} className="flex items-start">
                          <span className="inline-flex mr-3 flex-shrink-0 h-4 w-4 mt-1 rounded-full bg-[#ff39b3] items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-2.5 w-2.5 text-white"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                          <span className="text-sm text-gray-600 leading-relaxed mt-[0.5px]">
                            {intl.formatMessage({
                              id: point.id,
                              defaultMessage: point.defaultMessage,
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8">
                      <ButtonAuth
                        className="w-full"
                        onClick={() => navigate(option.link)}
                      >
                        <FormattedMessage
                          id={option.ctaId}
                          defaultMessage={option.ctaDefault}
                        />
                      </ButtonAuth>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 space-x-1 flex justify-center">
              <span>
                <FormattedMessage
                  id="AUTH.ALREADY.HAVE.ACCOUNT"
                  defaultMessage="Already have an account?"
                />
              </span>
              <Link
                to="/auth"
                className="cursor-pointer hover:text-pink underline"
                replace
              >
                <FormattedMessage
                  id="AUTH.SIGN_IN_HERE"
                  defaultMessage="Sign in here"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
